/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AssignmentProgressCard
 *
 * @file AssignmentProgressCard.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  AssignmentProgressCardRoot,
  AssignmentProgressCardHeader,
  AssignmentProgressCardTitleContainer,
  AssignmentProgressCardStatusContainer,
  AssignmentProgressCardFooter,
  AssignmentProgressCardTitle,
  AssignmentProgressCardContent,
  AssignmentProgressCardFooterRightText,
  AssignmentProgressCardFooterRightTextLine,
  AssignmentProgressCardProgressBar,
  AssignmentProgressCardProgressBarContainer,
  AssignmentProgressCardProgressBarWrapperButton,
  assignmentProgressCardTemplateComponents
} from './styles/AssignmentProgressCardStyles';
import { THEMES } from '../../common/constants';
import { BadgeTemplate, TextChipTemplate } from '../tabular/components/Template';
import ProgressBarWidget from '../common/ProgressBarWidget';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';
import mixins from '../../assets/styles/mixins.scss';
import TooltipWrapper from '../tooltip/TooltipWrapper';

const AssignmentProgressCard = (props) => {
  const {
    title,
    theme,
    sections,
    footer,
    status,
    segments,
    tooltipSegments,
    classes: customClasses,
    onCardClick,
    onStatusHover,
    onProgressBarHover,
    progressBarButtonTooltip
  } = props;

  const [isOverflowing, setIsOverflowing] = useState(true);
  const titleRef = useRef(null);

  useEffect(() => {
    const element = titleRef.current;
    if (element) {
      const originalHeight = element.clientHeight;
      element.style.webkitLineClamp = 'unset';
      setTimeout(() => {
        const fullHeight = element.clientHeight;
        setIsOverflowing(fullHeight > originalHeight);
        element.style.webkitLineClamp = 2;
      }, 0);
    }
  }, [titleRef.current?.clientHeight]);

  const templateComponents = assignmentProgressCardTemplateComponents;

  const renderTitle = () => {
    if (!title) return null;

    return (
      <AssignmentProgressCardTitleContainer>
        <TooltipWrapper
          tooltip={title}
          arrow
          interactive
          showTooltip={isOverflowing}
        >
          <AssignmentProgressCardTitle ref={titleRef}>{title}</AssignmentProgressCardTitle>
        </TooltipWrapper>
      </AssignmentProgressCardTitleContainer>
    );
  };

  const renderStatus = () => {
    if (!status?.label) return null;

    const isDarkTheme = theme === THEMES.DARK;
    const statusId = isDarkTheme ? `${status.id}-dark` : status.id;

    return (
      <AssignmentProgressCardStatusContainer
        onMouseEnter={() => {
          onStatusHover(props);
        }}
      >
        <BadgeTemplate
          topics={null}
          status={statusId}
          label={status.label}
          showIcon={status?.showIcon || false}
          classes={{
            chip: customClasses?.chip,
            badgeLabel: customClasses?.badgeLabel
          }}
          components={{ badge: templateComponents.badge }}
        />
      </AssignmentProgressCardStatusContainer>
    );
  };

  const renderHeader = () => (
    <AssignmentProgressCardHeader>
      {renderTitle()}
      {renderStatus()}
    </AssignmentProgressCardHeader>
  );

  const renderSections = () => {
    if (!sections) return null;

    return (
      <TextChipTemplate
        sections={sections.sections}
        label={sections.label}
        components={{
          sectionsContainer: templateComponents.sectionsContainer,
          sectionsLabel: templateComponents.sectionsLabel,
          sectionChip: templateComponents.sectionChip,
          sectionNumber: templateComponents.sectionNumber,
          textContainer: templateComponents.textContainer
        }}
      />
    );
  };

  const renderSegments = () => {
    if (!segments || segments.length === 0 || !tooltipSegments || tooltipSegments.length === 0) return null;

    const isDarkTheme = theme === THEMES.DARK;
    const segmentsProcessed = segments.map((segment) => ({
      ...segment,
      id: isDarkTheme ? `${segment.id}-dark` : segment.id
    }));

    return (
      <HtmlTooltip
        title={
          <StatusTemplate segments={tooltipSegments} isPercentage={false} />
        }
        arrow
        placement="bottom"
      >
        <AssignmentProgressCardProgressBarWrapperButton
          type="button"
          onFocus={(e) => {
            e.target.setAttribute('aria-expanded', true);
          }}
          onBlur={(e) => {
            e.target.setAttribute('aria-expanded', false);
          }}
          aria-label={progressBarButtonTooltip}
        >
          <AssignmentProgressCardProgressBar>
            <AssignmentProgressCardProgressBarContainer>
              <ProgressBarWidget
                segments={segmentsProcessed}
                backgroundColor={isDarkTheme ? mixins.darkProgressBgColor : mixins.accentColor}
                onHover={() => onProgressBarHover(props)}
              />
            </AssignmentProgressCardProgressBarContainer>
          </AssignmentProgressCardProgressBar>
        </AssignmentProgressCardProgressBarWrapperButton>
      </HtmlTooltip>
    );
  };

  const renderContent = () => (
    <AssignmentProgressCardContent>
      {renderSections()}
      {renderSegments()}
    </AssignmentProgressCardContent>
  );

  const renderFooterBadge = () => {
    if (!footer?.badge) return null;

    const FooterBadgeComponent = templateComponents.footerBadge;

    return (
      <FooterBadgeComponent
        label={footer.badge}
        size="small"
      />
    );
  };

  const parseFooterRightText = (text) => {
    if (!text) return null;

    const separatorIndex = text.indexOf(' @');
    if (separatorIndex === -1) {
      return { dateLine: text, timeLine: null };
    }

    return {
      dateLine: text.slice(0, separatorIndex),
      timeLine: text.slice(separatorIndex + 1)
    };
  };

  const renderFooterRightTextLines = (lines) => (
    <AssignmentProgressCardFooterRightText>
      {lines.map((line, index) => (
        <AssignmentProgressCardFooterRightTextLine key={index} component="span">
          {line}
        </AssignmentProgressCardFooterRightTextLine>
      ))}
    </AssignmentProgressCardFooterRightText>
  );

  const renderFooterRightText = () => {
    const { rightText } = footer || {};
    if (!rightText) return null;

    if (Array.isArray(rightText)) {
      return renderFooterRightTextLines(rightText);
    }

    if (typeof rightText !== 'string') {
      return (
        <AssignmentProgressCardFooterRightText>
          <AssignmentProgressCardFooterRightTextLine component="span">
            {rightText}
          </AssignmentProgressCardFooterRightTextLine>
        </AssignmentProgressCardFooterRightText>
      );
    }

    const parsed = parseFooterRightText(rightText);
    if (!parsed) return null;

    if (parsed.timeLine) {
      return (
        <AssignmentProgressCardFooterRightText>
          <AssignmentProgressCardFooterRightTextLine component="span">
            {parsed.dateLine}
          </AssignmentProgressCardFooterRightTextLine>
          <AssignmentProgressCardFooterRightTextLine component="span">
            {parsed.timeLine}
          </AssignmentProgressCardFooterRightTextLine>
        </AssignmentProgressCardFooterRightText>
      );
    }

    return (
      <AssignmentProgressCardFooterRightText>
        <AssignmentProgressCardFooterRightTextLine component="span">
          {parsed.dateLine}
        </AssignmentProgressCardFooterRightTextLine>
      </AssignmentProgressCardFooterRightText>
    );
  };

  const renderFooter = () => (
    <AssignmentProgressCardFooter>
      {renderFooterBadge()}
      {renderFooterRightText()}
    </AssignmentProgressCardFooter>
  );

  return (
    <AssignmentProgressCardRoot
      className={`theme-${theme || THEMES.DARK} ${customClasses?.card || ''}`}
      isButtonCard
      onClick={() => onCardClick(props)}
    >
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </AssignmentProgressCardRoot>
  );
};

AssignmentProgressCard.propTypes = {
  title: PropTypes.string,
  theme: PropTypes.string,
  sections: PropTypes.shape({
    label: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.string)
  }),
  footer: PropTypes.shape({
    badge: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    rightText: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node]))
    ])
  }),
  status: PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string,
    showIcon: PropTypes.bool
  }),
  segments: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
    type: PropTypes.string
  })),
  tooltipSegments: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
    type: PropTypes.string
  })),
  onCardClick: PropTypes.func,
  onStatusHover: PropTypes.func,
  onProgressBarHover: PropTypes.func,
  progressBarButtonTooltip: PropTypes.string,
  classes: PropTypes.shape({
    card: PropTypes.string,
    chip: PropTypes.string,
    badgeLabel: PropTypes.string,
    sectionsLabel: PropTypes.string
  })
};

AssignmentProgressCard.defaultProps = {
  title: '',
  theme: THEMES.DARK,
  sections: null,
  footer: null,
  status: null,
  segments: null,
  tooltipSegments: null,
  onCardClick: () => {},
  onStatusHover: () => {},
  onProgressBarHover: () => {},
  progressBarButtonTooltip: 'Performance Progress Bar',
  classes: {}
};

export default AssignmentProgressCard;
